import { useEffect, useState, useCallback } from "react";
import api, { fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AtSign, Trash2 } from "lucide-react";
import { toast } from "sonner";

export function TxNotesDialog({ txId, txLabel, open, onClose, onChanged }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]);
  const canWrite = ["ADMIN", "FINANCE"].includes(user?.role);

  const load = useCallback(() => {
    if (txId && open) api.get(`/banks/transactions/${txId}/notes`).then((r) => setNotes(r.data)).catch(() => {});
  }, [txId, open]);

  useEffect(() => {
    if (open) {
      load();
      setText("");
      setMentions([]);
      api.get("/org/mentionable-users").then((r) => setUsers(r.data)).catch(() => {});
    }
  }, [open, load]);

  const submit = async () => {
    if (!text.trim()) { toast.error("Escribe la anotación"); return; }
    try {
      await api.post(`/banks/transactions/${txId}/notes`, { text: text.trim(), mentions });
      setText("");
      setMentions([]);
      load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar la nota"); }
  };

  const remove = async (nid) => {
    try {
      await api.delete(`/banks/transactions/${txId}/notes/${nid}`);
      load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar"); }
  };

  const toggleMention = (uid) =>
    setMentions((m) => (m.includes(uid) ? m.filter((x) => x !== uid) : [...m, uid]));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="tx-notes-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Anotaciones del cargo</DialogTitle>
          <DialogDescription className="truncate" title={txLabel}>{txLabel || "Movimiento bancario"}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="tx-notes-list">
          {notes.length === 0 && <div className="text-sm text-slate-400 border border-dashed rounded-lg p-3">Sin anotaciones todavía.</div>}
          {notes.map((n) => (
            <div key={n.id} data-testid={`tx-note-${n.id}`} className="border border-slate-200 rounded-lg px-3 py-2 bg-slate-50/60">
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm whitespace-pre-wrap">{n.text}</div>
                {(n.author_id === user?.id || user?.role === "ADMIN") && (
                  <button data-testid={`tx-note-delete-${n.id}`} onClick={() => remove(n.id)}
                    className="text-slate-300 hover:text-red-600 transition-colors shrink-0">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-[11px] text-slate-400">{n.author_name} · {fmtDate(n.created_at)}</span>
                {(n.mention_names || []).map((m) => (
                  <Badge key={m} variant="outline" className="text-[10px] text-blue-700 border-blue-200 bg-blue-50">
                    <AtSign className="w-2.5 h-2.5 mr-0.5" />{m}
                  </Badge>
                ))}
              </div>
            </div>
          ))}
        </div>

        {canWrite && (
          <div className="space-y-2 pt-1">
            <textarea data-testid="tx-notes-input" rows={3} value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Escribe una anotación sobre este cargo..."
              className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#D9381E]/30" />
            {users.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="tx-notes-mentions">
                <span className="text-xs text-slate-400">Mencionar:</span>
                {users.map((u) => (
                  <button key={u.id} type="button" data-testid={`tx-notes-mention-${u.id}`} onClick={() => toggleMention(u.id)}
                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${mentions.includes(u.id)
                      ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 text-slate-500 hover:border-blue-300"}`}>
                    {u.name || u.email}
                  </button>
                ))}
              </div>
            )}
            <div className="flex justify-end">
              <Button data-testid="tx-notes-submit" size="sm" onClick={submit}
                className="bg-[#D9381E] hover:bg-[#B91C1C] text-white">Añadir nota</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
